/**
 * Helpers partagés entre les 3 étapes du RebillingWizard.
 */

export const eur = (n, p = 2) =>
  `${(Number(n) || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: p, maximumFractionDigits: p,
  })} €`;

export const VAT_RATES = [20, 10, 5.5, 2.1, 0];

export const SUGGESTED = [
  { label: "Appliquer 20% de marge", msg: "Applique 20% de marge sur toutes les lignes." },
  { label: "Arrondir au 10€ supérieur", msg: "Arrondis chaque PU HT au 10€ supérieur." },
  { label: "Regrouper toutes les lignes", msg: "Regroupe toutes les lignes en une seule ligne « Prestations refacturées »." },
  { label: "Marge forfaitaire 500€", msg: "Ajoute une marge forfaitaire de 500€ répartie sur toutes les lignes." },
  { label: "Passer TVA à 20%", msg: "Mets toutes les lignes à 20% de TVA." },
  { label: "Délai 45 jours", msg: "Fixe le délai de paiement à 45 jours." },
];

// Actions premium disponibles quand un template a été sélectionné (Step 2).
// Ces prompts sont écrits pour forcer l'IA à exploiter réellement le style
// du template (voir bloc TEMPLATE INSPIRATION du prompt backend).
export const SUGGESTED_WITH_TEMPLATE = [
  {
    label: "🎨 Appliquer le style du template",
    msg: "Applique intégralement le style du template : regroupe les lignes par matricule/référence (une ligne par machine et par type de service), reformule chaque libellé avec le même préfixe que le template (ex: « COPIE COULEUR REF: XXX »), simplifie en gardant uniquement la référence et enlève adresses/étages/compteurs, aligne la TVA sur celle du template, et détecte la marge implicite entre les PU source et les PU template pour l'appliquer.",
  },
  {
    label: "🔀 Regrouper par référence",
    msg: "Regroupe toutes les lignes par matricule/référence (chaque machine ou service = une ligne). Utilise les mêmes préfixes que le template.",
  },
  {
    label: "✂️ Simplifier les libellés",
    msg: "Simplifie chaque libellé : garde uniquement la nature du service et la référence (matricule). Retire adresses, étages, compteurs, dates, informations de contact.",
  },
  {
    label: "💰 Appliquer la marge du template",
    msg: "Détecte la marge implicite entre les PU source et les PU du template (ex: 0.034891→0.04 = ~15%). Applique cette marge sur toutes les lignes source.",
  },
  {
    label: "➕ Ajouter les frais de gestion",
    msg: "Ajoute les mêmes lignes de service que le template (frais de gestion, livraison, service additionnel) qui n'apparaissent pas dans la source.",
  },
];

/**
 * Recalcule les totaux (HT / TVA / TTC) d'un state à partir de ses lignes.
 * Applique la remise globale sur HT + TVA proportionnellement, alignement
 * strict avec compute_totals_extended côté backend.
 */
export function recomputeTotals(s) {
  let ht = 0, vat = 0;
  for (const ln of s.lines || []) {
    const net = Number(ln.quantity || 0) * Number(ln.unit_price_ht || 0) *
      (1 - Number(ln.discount_percent || 0) / 100);
    ht += net;
    vat += net * Number(ln.vat_rate || 0) / 100;
  }
  const gd = Number(s.global_discount_percent || 0);
  if (gd > 0) {
    const factor = 1 - gd / 100;
    ht *= factor;
    vat *= factor;
  }
  return {
    ...s,
    subtotal_ht: +ht.toFixed(2),
    total_vat: +vat.toFixed(2),
    total_ttc: +(ht + vat).toFixed(2),
    // Preserve `marge_appliquee_pct` — track cumulative user-applied margin
    // (survives across recomputeTotals calls to keep the indicator stable
    // when the user edits a single line manually).
    marge_appliquee_pct: s.marge_appliquee_pct ?? 0,
  };
}
